import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../core/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../core/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../../core/popover';
import { ExerciseType } from '@org/model';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ContentDescription } from '../../components/shared/content-description';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { renderInlineMarkdown } from '../../../custom/markdown';

interface SelectForBlankExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

const BLANK_MARKER = '___';

export function SelectForBlankExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: SelectForBlankExerciseProps) {
  const sentences = content.sentences ?? [];
  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.SelectForBlank) {
      return submission;
    }
    return null;
  }, [content.lastSubmission]);
  const lastSelections = useMemo(() => {
    if (!lastSubmission) {
      return [];
    }
    return lastSubmission.selections ?? [];
  }, [lastSubmission]);
  const lastEvaluationDetails = useMemo(() => {
    const evaluation = (content.lastEvaluation as any) ?? null;
    const autoDetails = (evaluation?.auto as any)?.details ?? evaluation?.details;
    return autoDetails?.kind === 'SelectForBlank' ? autoDetails : null;
  }, [content.lastEvaluation]);
  const [selections, setSelections] = useState<number[][]>(() =>
    sentences.map((sentence, sentenceIndex) => {
      const blanks = sentence.blanks ?? [];
      const sentenceSelections = lastSelections[sentenceIndex] ?? [];
      return blanks.map((_, blankIndex) => {
        const value = sentenceSelections[blankIndex];
        return typeof value === 'number' && value >= 0 ? value : -1;
      });
    })
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blankEvaluations, setBlankEvaluations] = useState<
    Array<
      Array<{
        isCorrect: boolean;
        correctLabel: string | null;
        grade?: 'perfect' | 'near' | 'partial' | 'wrong';
        reason?: string;
      }>
    >
  >([]);

  const blankSignature = useMemo(
    () => JSON.stringify(sentences.map((sentence) => (sentence.blanks ?? []).length)),
    [sentences]
  );

  useEffect(() => {
    setSelections(() =>
      sentences.map((sentence, sentenceIndex) => {
        const blanks = sentence.blanks ?? [];
        const sentenceSelections = lastSelections[sentenceIndex] ?? [];
        return blanks.map((_, blankIndex) => {
          const value = sentenceSelections[blankIndex];
          return typeof value === 'number' && value >= 0 ? value : -1;
        });
      })
    );
  }, [content.id, blankSignature, lastSelections]);

  const computeBlankEvaluations = useCallback(
    (submittedSelections: number[][]) =>
      sentences.map((sentence, sentenceIndex) => {
        const blanks = sentence.blanks ?? [];
        const sentenceSelections = submittedSelections[sentenceIndex] ?? [];

        return blanks.map((blank, blankIndex) => {
          const options = blank.options ?? [];
          const correctIndex = options.findIndex((option) => option.isCorrect);
          const correctLabel =
            correctIndex >= 0
              ? options[correctIndex]?.label ?? `Option ${correctIndex + 1}`
              : null;
          const submittedIndex = sentenceSelections[blankIndex];
          const isCorrect =
            typeof submittedIndex === 'number' &&
            submittedIndex >= 0 &&
            correctIndex >= 0 &&
            submittedIndex === correctIndex;
          const gradeFromBlank = (blank as any)?.grade as
            | 'perfect'
            | 'near'
            | 'partial'
            | 'wrong'
            | undefined;
          const reasonFromBlank = (blank as any)?.reason as string | undefined;

          return {
            isCorrect,
            correctLabel,
            grade: gradeFromBlank ?? (isCorrect ? 'perfect' : 'wrong'),
            reason: reasonFromBlank,
          };
        });
      }),
    [sentences],
  );

  const evaluationsFromPayload = useMemo(() => {
    if (!lastEvaluationDetails) {
      return null;
    }

    return (lastEvaluationDetails.sentences ?? []).map((sentence: any, sentenceIndex: number) => {
      const blanks = Array.isArray(sentence?.blanks) ? sentence.blanks : [];
      return blanks.map((blank: any, blankIndex: number) => {
        const correctOptionIndex = typeof blank?.correctOptionIndex === 'number' ? blank.correctOptionIndex : null;
        const contentOptions = sentences?.[sentenceIndex]?.blanks?.[blankIndex]?.options ?? [];
        const correctLabel =
          correctOptionIndex !== null
            ? contentOptions?.[correctOptionIndex]?.label ?? `Option ${correctOptionIndex + 1}`
            : null;

        return {
          isCorrect: Boolean(blank?.isCorrect),
          correctLabel,
          grade: blank?.grade as 'perfect' | 'near' | 'partial' | 'wrong' | undefined,
          reason: typeof blank?.reason === 'string' ? blank.reason : undefined,
        };
      });
    });
  }, [lastEvaluationDetails]);

  useEffect(() => {
    if (evaluationsFromPayload) {
      setBlankEvaluations(evaluationsFromPayload);
    } else if (lastSubmission) {
      setBlankEvaluations(computeBlankEvaluations(lastSubmission.selections ?? []));
    } else {
      setBlankEvaluations([]);
    }
  }, [computeBlankEvaluations, evaluationsFromPayload, lastSubmission]);

  const handleSelectionChange = (
    sentenceIndex: number,
    blankIndex: number,
    value: string
  ) => {
    if (isReadOnly) {
      return;
    }
    setSelections((previous) => {
      const next = previous.map((sentenceSelections) => [...sentenceSelections]);
      if (!next[sentenceIndex]) {
        next[sentenceIndex] = [];
      }
      next[sentenceIndex][blankIndex] = Number.parseInt(value, 10);
      return next;
    });
    setBlankEvaluations([]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type: ExerciseType.SelectForBlank,
        selections,
      });
      // Evaluations update via useEffect when content.lastEvaluation changes after cache invalidation
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = selections.length > 0;

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-4',
        variant === 'card' && 'rounded-lg border border-border bg-card p-4 shadow-sm'
      )}
    >
      {content.title && (
        <h3 className="text-lg font-semibold">
          {renderInlineMarkdown(content.title, 'select-for-blank-title')}
        </h3>
      )}
      <ContentDescription text={content.description} markdown />

      <div className="space-y-4">
        {sentences.map((sentence, sentenceIndex) => {
          const blanks = sentence.blanks ?? [];

          const parts = (sentence.sentence ?? '').split(BLANK_MARKER);

      const renderBlank = (blank: NonNullable<typeof blanks[number]>, selectIndex: number, key: string) => {
        const options = blank?.options ?? [];
        const evaluation = blankEvaluations[sentenceIndex]?.[selectIndex] as
          | { isCorrect: boolean; correctLabel?: string | null; grade?: 'perfect' | 'near' | 'partial' | 'wrong'; reason?: string }
          | undefined;
        const grade = evaluation ? evaluation.grade ?? (evaluation.isCorrect ? 'perfect' : 'wrong') : null;
        const correctionMessage =
          evaluation?.reason ||
          (evaluation?.correctLabel
            ? `Correct answer: ${evaluation.correctLabel}`
            : 'Review the sentence and try again.');

        const theme =
          grade === 'perfect'
            ? {
                border: 'border-emerald-300',
                bg: 'bg-emerald-50/70 text-emerald-800',
                ring: 'border-emerald-300 bg-background text-emerald-600',
                icon: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
                title: 'Looks good',
                iconType: 'success' as const,
              }
            : grade === 'near' || grade === 'partial'
              ? {
                  border: 'border-amber-300',
                  bg: 'bg-amber-50/70 text-amber-900',
                  ring: 'border-amber-300 bg-background text-amber-700',
                  icon: 'bg-amber-100 text-amber-800 border border-amber-200',
                  title: grade === 'near' ? 'Almost there' : 'Partially correct',
                  iconType: 'warning' as const,
                }
              : grade === 'wrong'
                ? {
                    border: 'border-destructive/50',
                    bg: 'bg-destructive/10 text-destructive',
                    ring: 'border-destructive/50 bg-background text-destructive',
                    icon: 'bg-rose-100 text-rose-700 border border-rose-200',
                    title: 'Needs review',
                    iconType: 'error' as const,
                  }
                : {
                    border: 'border-border',
                    bg: 'bg-background/60 text-foreground',
                    ring: 'border-border bg-background text-muted-foreground',
                    icon: 'bg-muted text-muted-foreground border border-border/70',
                    title: 'Pending',
                    iconType: 'pending' as const,
                  };

        return (
          <div key={key} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Select
                value={
                  selections[sentenceIndex]?.[selectIndex] !== undefined &&
                  selections[sentenceIndex][selectIndex] >= 0
                    ? String(selections[sentenceIndex][selectIndex])
                    : undefined
                }
                onValueChange={(value) => handleSelectionChange(sentenceIndex, selectIndex, value)}
                disabled={options.length === 0 || isReadOnly}
              >
                <SelectTrigger
                  className={cn(
                    'w-44 min-w-[8rem] max-w-full',
                    theme.border,
                    theme.bg,
                  )}
                >
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((option, optionIndex) => (
                    <SelectItem
                      key={option.optionId ?? `${sentenceIndex}-${selectIndex}-${optionIndex}`}
                      value={String(optionIndex)}
                    >
                      {renderInlineMarkdown(
                        option.label || `Option ${optionIndex + 1}`,
                        `${content.id}-sentence-${sentenceIndex}-select-${selectIndex}-option-${optionIndex}`,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {grade && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'shrink-0 rounded-full border p-1.5 shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                        theme.ring,
                        theme.icon,
                      )}
                      aria-label="View feedback"
                    >
                      {theme.iconType === 'success' ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : (
                        <AlertCircle className="h-5 w-5" />
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    sideOffset={8}
                    className="w-64 space-y-2 text-sm leading-relaxed"
                  >
                    <p className="font-medium">{theme.title}</p>
                  <p className="text-muted-foreground">
                    {renderInlineMarkdown(
                      correctionMessage,
                      `${content.id}-select-correction-${sentenceIndex}-${selectIndex}`,
                    )}
                  </p>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
        );
      };

          return (
            <div
              key={sentence.sentenceId ?? `${content.id}-sentence-${sentenceIndex}`}
              className="space-y-2 rounded-md border border-dashed border-border/80 bg-background/60 p-3"
            >
              <p className="text-sm font-medium text-muted-foreground">
                {/* Sentence {sentenceIndex + 1} */}
              </p>
              <div className="flex flex-wrap items-center gap-2 whitespace-pre-wrap text-base leading-relaxed">
                {parts.map((part, partIndex) => (
                  <Fragment key={`${sentenceIndex}-part-${partIndex}`}>
                    {part &&
                      renderInlineMarkdown(
                        part,
                        `${content.id}-sentence-${sentenceIndex}-part-${partIndex}`,
                      )}
                    {partIndex < blanks.length && (
                      renderBlank(
                        blanks[partIndex]!,
                        partIndex,
                        blanks[partIndex]?.blankId ??
                          `${sentenceIndex}-${partIndex}-${content.id}`,
                      )
                    )}
                  </Fragment>
                ))}
                {parts.length - 1 < blanks.length &&
                  blanks.slice(parts.length - 1).map((blank, blankIndex) => {
                    const selectIndex = (parts.length - 1) + blankIndex;
                    return (
                      renderBlank(
                        blank,
                        selectIndex,
                        blank.blankId ?? `${sentenceIndex}-tail-${blankIndex}`,
                      )
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>

      <ExerciseFeedbackList feedback={feedback ?? []} pending={isSubmitting} />
      {onExerciseSubmit && !isReadOnly && (
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            variant="submit"
            size="submit"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            className="w-full sm:w-auto"
          >
            Submit answers
          </Button>
        </div>
      )}
    </form>
  );
}
